import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../common/hooks/hooks";
import { selectCategoryTree } from "../../productSelectors";
import { findCategoryInTree } from "../../helpers/categoryTreeHelpers";

/**
 * Renders the H1 Title and Category Description for SEO purposes.
 * Automatically switches to a generic "Shop" title if multiple categories or no categories are selected.
 */
const CategoryHeader: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const filters = useAppSelector((state) => state.product.filters);
  const categoryTree = useAppSelector(selectCategoryTree);

  // Determine if a single specific category is active
  const activeCategory = useMemo(() => {
    // Only resolve a specific category if exactly ONE is selected
    if (filters.category.length === 1 && categoryTree.length > 0) {
      return findCategoryInTree(filters.category[0], categoryTree);
    }
    return null;
  }, [filters.category, categoryTree]);

  if (!areTranslationsReady) {
    return null;
  }

  // State 1: Multiple categories selected or no category selected
  // We render a generic Shop H1 title for accessibility and SEO
  if (!activeCategory) {
    return (
      <div className="products-archive__header">
        <h1 className="products-archive__h1-title">
          {t("productsArchive.title", "Shop")}
        </h1>
      </div>
    );
  }

  // State 2: A single category is selected
  // We render its specific name as an H1, and output its rich description
  return (
    <div className="products-archive__header products-archive__header--category">
      <h1 className="products-archive__h1-title">{activeCategory.name}</h1>

      {activeCategory.description && (
        <div
          className="products-archive__description wysiwyg-content"
          dangerouslySetInnerHTML={{ __html: activeCategory.description }}
        />
      )}
    </div>
  );
};

export default CategoryHeader;
